/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright (c) 2026 Steven Warehouse Project. All Rights Reserved.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Suspense } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import {
  HomeIcon,
  CubeIcon,
  CircleStackIcon,
  ArrowsRightLeftIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  UserCircleIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  SignalIcon,
  PlayIcon, FilmIcon,
  LinkIcon,
  KeyIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  PhotoIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  external?: boolean;
  externalUrl?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
}

const navigation: NavSection[] = [
  {
    title: '',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    ],
  },
  {
    title: 'Audience',
    items: [
      { name: 'Audience Insights', href: '/audience-insights', icon: ChartBarIcon },
      { name: 'Profile Explorer', href: '/profile-explorer', icon: UserCircleIcon },
      { name: 'Segments', href: '/segments', icon: RectangleGroupIcon },
      { name: 'Podcast Ad Insights', href: '/podcast-insights', icon: MegaphoneIcon },
      { name: 'Social Insights', href: '/social-insights', icon: ChatBubbleLeftRightIcon },
    ],
  },
  {
    title: 'Content',
    items: [
      { name: 'YouTube Insights', href: '/youtube-insights', icon: PlayIcon },
      { name: 'Episode Insights', href: '/episode-insights', icon: FilmIcon },
      { name: 'Commercial Pacing', href: '/commercial-pacing', icon: CurrencyDollarIcon },
      { name: 'Monthly Report', href: '/monthly-report', icon: CalendarDaysIcon },
      { name: 'Guest Radar', href: '/guest-radar', icon: SignalIcon },
      { name: 'Research Agent', href: '/research-agent', icon: BeakerIcon },
      { name: 'Episode Search', href: '/episode-search', icon: MagnifyingGlassIcon },
      { name: 'Link Manager', href: '/link-manager', icon: LinkIcon },
      { name: 'Thumbnail Generator', href: '/thumbnail-generator', icon: PhotoIcon },
    ],
  },
  {
    title: 'Data Transfer',
    items: [
      { name: 'Sources', href: '/sources', icon: CubeIcon },
      { name: 'Destinations', href: '/destinations', icon: CircleStackIcon, adminOnly: true },
      { name: 'Pipelines', href: '/connections', icon: ArrowsRightLeftIcon, adminOnly: true },
    ],
  },
  {
    title: 'External Resources',
    items: [
      { name: 'BI Dashboard', icon: ArrowTopRightOnSquareIcon, external: true, externalUrl: 'https://steven-audience-radar.metabaseapp.com' },
      { name: 'Link Tracking', icon: ArrowTopRightOnSquareIcon, external: true, externalUrl: 'https://linklyhq.com' },
    ],
  },
  {
    title: 'Settings',
    items: [
      { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
      { name: 'MCP Credentials', href: '/mcp-credentials', icon: KeyIcon, adminOnly: true },
    ],
  },
];

// Pages that need extra-wide layout (no max-width constraint)
const WIDE_PAGES = ['/dashboard', '/podcast-insights', '/audience-insights', '/youtube-insights', '/social-insights', '/episode-insights', '/commercial-pacing', '/monthly-report', '/segments', '/profile-explorer', '/sources', '/destinations', '/connections', '/link-manager'];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [airflowUrl, setAirflowUrl] = useState<string | null>(null);
  const isWidePage = WIDE_PAGES.some(p => location.pathname.startsWith(p));
  // When viewing a segment in Profile Explorer, highlight Segments nav instead
  const isSegmentView = location.pathname === '/profile-explorer' && new URLSearchParams(location.search).has('segment');

  useEffect(() => {
    if (user) {
      api.get<{ airflowUrl: string | null }>('/config')
        .then(data => setAirflowUrl(data.airflowUrl))
        .catch(() => {});
    }
  }, [user]);

  return (
    <div className="h-screen flex bg-surface-950 overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-surface-900 border-r border-surface-800
          transform transition-transform duration-200 ease-out
          lg:translate-x-0 lg:static lg:z-auto lg:flex-shrink-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-14 px-4 border-b border-surface-800">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <ellipse cx="12" cy="6" rx="8" ry="3" fill="white" opacity="0.9"/>
                  <path d="M4 6v4c0 1.66 3.58 3 8 3s8-1.34 8-3V6c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.7"/>
                  <path d="M4 10v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.5"/>
                  <path d="M4 14v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.35"/>
                </svg>
              </div>
              <span className="font-semibold text-surface-100">Creator Insights</span>
            </div>
            <button
              className="lg:hidden p-1 text-surface-400 hover:text-surface-100"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {navigation.map((section, sectionIdx) => {
              // Filter items based on admin status
              let visibleItems = section.items.filter(item => !item.adminOnly || isAdmin);
              
              // Add dynamic Airflow link to External Resources section if configured (admin only)
              if (section.title === 'External Resources' && isAdmin && airflowUrl) {
                visibleItems = [
                  ...visibleItems,
                  { 
                    name: 'Airflow', 
                    icon: ArrowTopRightOnSquareIcon, 
                    external: true, 
                    externalUrl: airflowUrl,
                    adminOnly: true,
                  },
                ];
              }
              
              // Skip empty sections
              if (visibleItems.length === 0) return null;
              
              return (
                <div key={section.title || sectionIdx} className={sectionIdx > 0 ? 'mt-6' : ''}>
                  {/* Section Header */}
                  {section.title && (
                    <div className="px-3 mb-2">
                      <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wide">
                        {section.title}
                      </h3>
                    </div>
                  )}
                  
                  {/* Section Items */}
                  <div className="space-y-0.5">
                    {visibleItems.map((item) => (
                      item.external ? (
                        <a
                          key={item.name}
                          href={item.externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-surface-300 hover:text-surface-100 hover:bg-surface-800 transition-colors"
                        >
                          <item.icon className="w-5 h-5" />
                          {item.name}
                          <span className="ml-auto text-xs text-surface-500">↗</span>
                        </a>
                      ) : (
                        <NavLink
                          key={item.name}
                          to={item.href!}
                          onClick={() => setSidebarOpen(false)}
                          className={({ isActive }) => {
                            // When viewing a segment in Profile Explorer, Segments should be active, not Profile Explorer
                            let active = isActive;
                            if (isSegmentView) {
                              if (item.href === '/segments') active = true;
                              if (item.href === '/profile-explorer') active = false;
                            }
                            return `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              active
                                ? 'bg-primary-600/10 text-primary-400'
                                : 'text-surface-300 hover:text-surface-100 hover:bg-surface-800'
                            }`;
                          }}
                        >
                          <item.icon className="w-5 h-5" />
                          {item.name}
                        </NavLink>
                      )
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-surface-800">
            <div className="flex items-center gap-3 px-2 py-2">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
                  {user?.displayName?.[0] || user?.email?.[0] || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-surface-100 truncate">
                  {user?.displayName || 'User'}
                </p>
                <div className="flex items-center gap-2">
                <p className="text-xs text-surface-400 truncate">{user?.email}</p>
              {isAdmin && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-600/10 text-primary-400 rounded">
                  Admin
                </span>
              )}
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm font-medium text-surface-300 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between h-14 px-4 bg-surface-900 border-b border-surface-800">
          <button
            className="p-2 text-surface-400 hover:text-surface-100"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                <ellipse cx="12" cy="6" rx="8" ry="3" fill="white" opacity="0.9"/>
                <path d="M4 6v4c0 1.66 3.58 3 8 3s8-1.34 8-3V6c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.7"/>
                <path d="M4 10v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.5"/>
                <path d="M4 14v4c0 1.66 3.58 3 8 3s8-1.34 8-3v-4c0 1.66-3.58 3-8 3s-8-1.34-8-3z" fill="white" opacity="0.35"/>
              </svg>
            </div>
            <span className="font-semibold text-surface-100 text-sm">Creator Insights</span>
          </div>
          <div className="w-9" />
        </header>

        {/* Page content */}
        <main className={`flex-1 overflow-y-auto ${isWidePage ? 'p-3 lg:p-4' : 'p-6 lg:p-8'}`}>
          <div className={`animate-fade-in ${isWidePage ? 'max-w-none' : 'max-w-5xl mx-auto'}`}>
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center h-64 gap-4 animate-pulse">
                  <div className="relative">
                    <div className="w-12 h-12 border-2 border-surface-700 border-t-primary-500 rounded-full animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg opacity-50" />
                    </div>
                  </div>
                  <p className="text-sm text-surface-400">Loading page...</p>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>

      </div>
    </div>
  );
}
